<script context="module" lang="ts">
    import {
        hourlyForecast,
        conditions,
        widgetInfo,
        organizationLocation,
        OrganizationLocation,
        Forecast,
        Observation,
        Widget,
        ForecastType,
        dailyForecast,
        mapImageBase64String,
        radarImageBase64Strings,
        lightningDelay,
        lightningImageBase64String,
        updated,
        customerStatus,
        lightningDelayError,
        Pollutant,
        aqiData,
    } from "./store";
    import {
        LIGHTNING_REFRESH_MS,
        parseLightningDelaySeconds,
        shouldApplyServerDelay,
        shouldShowLightningError,
    } from "./refresh";

    let baseUrl: string = "https://localhost:7067";
    // 0 = never succeeded. One in-flight GET; hang aborts at LIGHTNING_REFRESH_MS.
    let lightningLastSuccessAt = 0;
    let lightningDelayInFlight = false;
    let envUrl = process.env.WIDGET_API_URL;
    if (envUrl) {
        baseUrl = envUrl;
    }

    interface PerryApiResponse<T> {
        message?: string;
        isError?: boolean;
        responseException?: any;
        data?: T;
    }

    export async function getOrgLocationForWidget(orgLocationId: string) {
        let orgLocationResponse: PerryApiResponse<OrganizationLocation> = null;
        try {
            if (!orgLocationId) throw new Error("No org location id provided.");

            const response = await fetch(
                baseUrl + "/Widget/OrganizationLocation/" + orgLocationId,
            );
            const json = await response.json();
            orgLocationResponse =
                json as PerryApiResponse<OrganizationLocation>;

            if (!orgLocationResponse || !orgLocationResponse.data)
            throw new Error("No forecast data returned.");

            organizationLocation.set(orgLocationResponse.data);
        } catch (error) {
            console.error("Unable to get forecast for widget: " + error);
        }
    }

    export async function getForecastForWidget(
        orgLocId: string,
        forecastType: ForecastType,
    ) {
        let forecastResponse: PerryApiResponse<Array<Forecast>> = null;
        try {
            if (!orgLocId) throw new Error("No org location id provided.");

            const response = await fetch(
                baseUrl +
                    "/Widget/Forecast/" +
                    orgLocId +
                    "?forecastType=" +
                    forecastType,
            );
            const json = await response.json();
            forecastResponse = json as PerryApiResponse<Array<Forecast>>;
            if (!forecastResponse || !forecastResponse.data)
            throw new Error("No forecast data returned.");

            if (forecastType === ForecastType.HOURLY) {
                hourlyForecast.set(forecastResponse.data);
            }
            if (forecastType === ForecastType.DAILY) {
                dailyForecast.set(forecastResponse.data);
            }
        } catch (error) {
            console.error("Unable to get forecast for widget: " + error);
        }
    }

    export async function getConditionsForWidget(orgLocId: string) {
        try {
            if (!orgLocId) throw new Error("No org location id provided.");

            const response = await fetch(
                baseUrl + "/Widget/Conditions/" + orgLocId,
            );
            let conditionsData = await response.json();
            var observation: Observation = conditionsData.data;
            observation.weatherStation = conditionsData.weatherStation ?? null;
            conditions.set(observation);
        } catch (error) {
            console.log("Unable to get conditions for widget: " + error);
        }
    }

    export async function getAqiForWidget(orgLocId: string) {
        let aqiResponse: Pollutant[] = null;

        try {
            if (!orgLocId) throw new Error("No org location id provided.");

            const response = await fetch(
                baseUrl + "/Widget/Aqi/" + orgLocId,
            );

            const json = await response.json();

            aqiResponse = json as Pollutant[];

            aqiData.set(aqiResponse);
        } catch (error) {
            console.error("Unable to get AQI for widget: " + error);
        }
    }

    export async function getWidgetInfo(widgetId: string, host: string) {
        let widgetResponse: Widget = null;
        try {
            if (!widgetId) throw new Error("No widget id provided.");

            let response = await fetch(baseUrl + "/Widget/" + widgetId, {
                headers: { "widget-host": host },
            });
            let json = await response.json();
            if (json.System && json.System.length > 0) {
                throw new Error(json.System[0]);
            }
            widgetResponse = json as Widget;

            //Check if the widget is using the new policy architecture
            let useNewPolicyArch = widgetResponse.primaryPolicyTriggerId;

            // Only change the updated time if we successfully get the widget info.
            updated.set(new Date());
            if (!widgetResponse) throw new Error("Widget not found.");
            if(!useNewPolicyArch){
                if (widgetResponse.lightningAllClearMinutes == undefined)
                throw new Error("Widget policy group not found.");
            }

            widgetInfo.set(widgetResponse);
        } catch (error) {
            console.error(error);
        }
    }

    export async function getMapImage(
        latitude: number,
        longitude: number,
        radius: number = 10,
        width: number = 0,
        height: number = 0,
    ) {
        let mapResponse: string = null;
        try {
            let response = await fetch(
                baseUrl +
                    "/Widget/Image?latitude=" +
                    latitude +
                    "&longitude=" +
                    longitude +
                    "&radius=" +
                    radius +
                    "&width=" +
                    width +
                    "&height=" +
                    height,
            );
            let text = await response.text();
            mapResponse = text as string;
            
            if (!mapResponse) throw new Error("Error getting map image.");

            mapImageBase64String.set(mapResponse);
        } catch (error) {
            console.error("Unable to get map image: " + error);
        }
    }

    export async function getRadarImages(
        latitude: number,
        longitude: number,
        radius: number = 10,
        width: number = 0,
        height: number = 0,
    ) {
        let radarResponse: string[] = null;
        try {
            let response = await fetch(
                baseUrl +
                    "/Widget/Image/Radar?latitude=" +
                    latitude +
                    "&longitude=" +
                    longitude +
                    "&radius=" +
                    radius +
                    "&width=" +
                    width +
                    "&height=" +
                    height,
            );
            let text = await response.json();
            radarResponse = text as string[];
            radarImageBase64Strings.set(radarResponse);

            if (!radarResponse) throw new Error("Error getting map image.");
        } catch (error) {
            console.error("Unable to get map image: " + error);
        }
    }

    export async function getLightningImage(
        latitude: number,
        longitude: number,
        radius: number = 10,
        width: number = 0,
        height: number = 0,
    ) {
        let lightningImageResponse: string = null;
        try {
            let response = await fetch(
                baseUrl +
                    "/Widget/Image/Lightning?latitude=" +
                    latitude +
                    "&longitude=" +
                    longitude +
                    "&radius=" +
                    radius +
                    "&width=" +
                    width +
                    "&height=" +
                    height,
            );
            let text = await response.text();
            lightningImageResponse = text as string;
            lightningImageBase64String.set(lightningImageResponse);

            if (!lightningImageResponse)
            throw new Error("Error getting map image.");
        } catch (error) {
            console.error("Unable to get lightning image: " + error);
        }
    }

    export async function getLightningDelay(widgetId: string) {
        if (lightningDelayInFlight) return;
        lightningDelayInFlight = true;
        const controller = new AbortController();
        const timeoutId = setTimeout(
            () => controller.abort(),
            LIGHTNING_REFRESH_MS,
        );
        try {
            let response = await fetch(
                baseUrl + "/Widget/LightningDelay/" + widgetId,
                { signal: controller.signal },
            );

            if (response.status != 200) {
                throw new Error("unable to get lightning delay");
            }

            const delaySeconds = parseLightningDelaySeconds(await response.json());
            if (delaySeconds == null) {
                throw new Error("unable to get lightning delay");
            }

            const hadSuccess = lightningLastSuccessAt > 0;
            lightningDelay.update((current) => {
                if (!hadSuccess) return delaySeconds;
                return shouldApplyServerDelay(current, delaySeconds)
                    ? delaySeconds
                    : current;
            });
            lightningLastSuccessAt = Date.now();
            lightningDelayError.set(false);
        } catch (error) {
            console.log("unable to get lightning delay: " + error);
            // Keep last good UI through blips. Unavailable only before the first success
            // or after flying blind for a full widget refresh interval.
            if (
                shouldShowLightningError(
                    lightningLastSuccessAt > 0,
                    lightningLastSuccessAt > 0
                        ? Date.now() - lightningLastSuccessAt
                        : 0,
                )
            ) {
                lightningDelayError.set(true);
            }
        } finally {
            clearTimeout(timeoutId);
            lightningDelayInFlight = false;
        }
    }

    export async function getCustomerStatus(widgetId: string) {
        try {
            if (!widgetId) throw new Error("No widget id provided.");

            let response = await fetch(
                baseUrl + "/Widget/CustomerStatus/" + widgetId,
            );
            let status = await response.json();
            customerStatus.set(status);
            if (status === 1) {
                console.log("Inactive customer.");
                throw new Error("Inactive customer.");
            }
        } catch (error) {
            console.error("Unable to display widget: " + error);
        }
    }
    
</script>
